import { createContext, useContext, useEffect, useRef, useState } from 'react';
import axios from 'axios';

/* ------------------------------------------------------------------ */
/*  Inactivity                                                         */
/* ------------------------------------------------------------------ */

/**
 * Signed out after 30 minutes with no mouse, keyboard, touch or scroll.
 * Matches MIA_IDLE_TIMEOUT in session_bootstrap.php.
 *
 * The server measures time since the last REQUEST. That alone is not enough:
 * someone reading a long page makes no requests and would be dropped, and
 * pages that poll in the background would keep an unattended screen signed in
 * for ever. So the browser measures real input, keeps the server's clock in
 * step while the person is active, and signs out properly when they are not.
 */
const IDLE_LIMIT_MS = 30 * 60 * 1000;

/** While active, tell the server at most this often. */
const HEARTBEAT_MS = 4 * 60 * 1000;

/**
 * Shared by every open tab, so working in one tab keeps the others signed in -
 * otherwise an idle tab would sign you out of the one you are using.
 */
const ACTIVITY_KEY = 'mia.lastActivityAt';

/** Read by the login page to explain why you are looking at it. */
export const SIGNED_OUT_REASON_KEY = 'mia.signedOutReason';

const readActivity = () => {
  try {
    return Number(localStorage.getItem(ACTIVITY_KEY)) || 0;
  } catch {
    return 0;
  }
};

const writeActivity = (t) => {
  try {
    localStorage.setItem(ACTIVITY_KEY, String(t));
  } catch {
    // Private mode or storage blocked: this tab's own timer still works.
  }
};

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [authState, setAuthState] = useState({
    isAuthenticated: false,
    userRole: null,
    userID: null,
    isLoading: true, // Only for initial load
  });


  // Single source of truth for auth status
  const checkAuth = async () => {
    try {
      const authStatusString = localStorage.getItem('authStatus');
      
      if (!authStatusString) {
        return { isAuthenticated: false, userRole: null, userID: null };
      }

      const authStatus = JSON.parse(authStatusString);
      return { 
        isAuthenticated: authStatus.isLoggedIn, 
        userRole: authStatus.role,
        userID: authStatus.Id 
      };
    } catch (err) {
      console.error('Auth check error:', err);
      return { isAuthenticated: false, userRole: null, userID: null };
    }
  };

  // Initialize auth state only once
  useEffect(() => {
    const initializeAuth = async () => {
      const { isAuthenticated, userRole, userID } = await checkAuth();
      setAuthState({
        isAuthenticated,
        userRole,
        userID,
        isLoading: false
      });

      // Then reconcile with the server. If the PHP session has gone this
      // flips the app to signed-out instead of leaving it stuck looking
      // signed in, which is what hid the broken session for so long.
      await revalidateWithServer();
    };

    initializeAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array ensures this runs only once

  const login = async (credentials) => {
    try {
      const formData = new FormData();
      formData.append('email', credentials.email);
      formData.append('password', credentials.password);
      formData.append('captcha', credentials.captcha);
      formData.append('action', 'login');
      formData.append('token', `${process.env.REACT_APP_API_TOKEN}`);

      const response = await axios.post(
        'https://mindsinaction.com.na/api/mindsbackend/proccess.php',
        formData,
        { withCredentials: true }
      );
      
      if (response.data?.success) {
        const userRole = response.data.data?.user_role;
        const userID = response.data.data?.user_id;
        localStorage.setItem('authStatus', JSON.stringify({
          isLoggedIn: true,
          role: userRole,
          Id: userID
        }));
        
        setAuthState({
          isAuthenticated: true,
          userRole,
          userID,
          isLoading: false
        });
        
        return { success: true, role: userRole, Id: userID };
      }
      return { success: false, data: response };
    } catch (err) {
      console.error('Login error:', err);
      return { success: false };
    }
  };

  /**
   * What the SERVER thinks, which is the only authority that matters.
   *
   * checkAuth() above reads localStorage, and localStorage is only a cache - it
   * cannot know that the PHP session has gone. That is how the app ended up
   * showing somebody as signed in while every API call came back with no
   * session behind it.
   *
   * Three outcomes, deliberately handled differently:
   *
   *   authenticated      trust the server and refresh the cache from it
   *   NOT authenticated  sign out locally; the cache was stale
   *   call failed        keep the cache. A dropped connection or a 500 is not
   *                      evidence that somebody is logged out, and throwing
   *                      them out over it would be worse than the bug this
   *                      function exists to fix.
   */
  const revalidateWithServer = async () => {
    try {
      const response = await axios.get(
        'https://mindsinaction.com.na/api/mindsbackend/proccess.php',
        {
          params: { action: 'check_auth', token: `${process.env.REACT_APP_API_TOKEN}` },
          withCredentials: true
        }
      );

      const data = response?.data?.data ?? {};

      if (response?.data?.success && data.isLoggedIn) {
        // user_id is now sent explicitly; user.Id is the fallback for a server
        // still running the older checkAuthStatus().
        const userID = data.user_id ?? data.user?.Id ?? null;
        const userRole = data.user_role ?? null;

        localStorage.setItem('authStatus', JSON.stringify({
          isLoggedIn: true,
          role: userRole,
          Id: userID
        }));

        setAuthState({
          isAuthenticated: true,
          userRole,
          userID,
          isLoading: false
        });
        return true;
      }

      // An explicit "no" from the server. The cache was lying - clear it, which
      // bounces the user to the login page instead of leaving them sitting on a
      // page where nothing will ever load.
      localStorage.removeItem('authStatus');
      setAuthState({
        isAuthenticated: false,
        userRole: null,
        userID: null,
        isLoading: false
      });
      return false;
    } catch (err) {
      // Could not ask. Leave whatever the cache said alone.
      console.warn(
        'Auth revalidation failed, keeping the cached session:',
        err?.message
      );
      setAuthState(prev => ({ ...prev, isLoading: false }));
      return null;
    }
  };

  const logout = async () => {
    try {
      /**
       * A form, not JSON, and with the token. proccess.php only reads the API
       * token from form fields; the JSON body this used to send came back
       * "Unauthorized access", so the SERVER session was never ended - the
       * browser forgot the login while the server kept it.
       */
      const form = new FormData();
      form.append('action', 'logout');
      form.append('token', `${process.env.REACT_APP_API_TOKEN}`);
      await axios.post(
        'https://mindsinaction.com.na/api/mindsbackend/proccess.php',
        form,
        { withCredentials: true }
      );
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      localStorage.removeItem('authStatus');
      setAuthState({
        isAuthenticated: false,
        userRole: null,
        userID: null,   // was omitted, leaving a stale id in the context
        isLoading: false
      });
    }
  };

  /* ---------------------------------------------------------------- */
  /*  Keep the session alive while it is being used - and only then     */
  /* ---------------------------------------------------------------- */

  const lastWriteRef = useRef(0);
  const lastBeatRef = useRef(0);
  // Set the moment an idle sign-out starts. logout() waits on the network
  // before it marks the user signed out, and without this a tick (or a return
  // to the tab) landing in that gap started a second sign-out.
  const signingOutRef = useRef(false);

  useEffect(() => {
    if (!authState.isAuthenticated) return undefined;

    signingOutRef.current = false;

    // Signing in, or loading/refreshing a page, is activity.
    const start = Date.now();
    writeActivity(start);
    lastWriteRef.current = start;
    lastBeatRef.current = start;

    const markActive = () => {
      const now = Date.now();
      // Every mouse move would be a lot of writes; every 15 s is plenty.
      if (now - lastWriteRef.current > 15000) {
        lastWriteRef.current = now;
        writeActivity(now);
      }
    };

    const tick = async () => {
      if (signingOutRef.current) return;
      const now = Date.now();
      // The most recent input in ANY tab.
      const last = Math.max(readActivity(), lastWriteRef.current);

      if (now - last >= IDLE_LIMIT_MS) {
        signingOutRef.current = true;
        try {
          sessionStorage.setItem(SIGNED_OUT_REASON_KEY, 'idle');
        } catch {
          // no message on the login page, but still signed out
        }
        await logout();
        return;
      }

      // Recent input, but no request for a while (reading, typing a long
      // note): touch the server so its own 30 minutes does not run out
      // under somebody who is plainly still here.
      if (now - last < HEARTBEAT_MS && now - lastBeatRef.current >= HEARTBEAT_MS) {
        lastBeatRef.current = now;
        revalidateWithServer();
      }
    };

    // A laptop waking from sleep, or coming back to the tab, checks at once
    // rather than waiting for the next tick.
    const onVisible = () => {
      if (document.visibilityState === 'visible') tick();
    };

    // Signed out in another tab: follow it here too.
    const onStorage = (e) => {
      if (e.key === 'authStatus' && !e.newValue) {
        setAuthState({ isAuthenticated: false, userRole: null, userID: null, isLoading: false });
      }
    };

    const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'wheel', 'touchstart'];
    events.forEach((ev) => window.addEventListener(ev, markActive, { passive: true }));
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('storage', onStorage);
    const timer = setInterval(tick, 30000);

    return () => {
      events.forEach((ev) => window.removeEventListener(ev, markActive));
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('storage', onStorage);
      clearInterval(timer);
    };
    // logout and revalidateWithServer only use setAuthState and axios, which
    // never change; re-running on their identity would reset the timers on
    // every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authState.isAuthenticated]);

  const getTeams = async () => {
    try {
      const response = await axios.get(
        'https://mindsinaction.com.na/api/mindsbackend/proccess.php',
        {
          params: { action: 'getTeam', token: `${process.env.REACT_APP_API_TOKEN}` },
          withCredentials: true
        }
      );

      if (response.data?.success) {
        return { 
          success: true, 
          data: response.data.data,
          message: response.data.message 
        };
      }
      return { 
        success: false, 
        message: response.data?.message || "Unknown error" 
      };
    } catch (err) {
      console.error('Error loading Team Data:', err);
      return { 
        success: false, 
        message: err.response?.data?.message || "Network error" 
      };
    }
  };

  const getProducts = async () => {
    try {
      const response = await axios.get(
        'https://mindsinaction.com.na/api/mindsbackend/proccess.php',
        {
          params: { action: 'getProducts', token: `${process.env.REACT_APP_API_TOKEN}` },
          withCredentials: true
        }
      );

      if (response.data?.success) {
        return { 
          success: true, 
          data: response.data.data,
          message: response.data.message 
        };
      }
      return { 
        success: false, 
        message: response.data?.message || "Unknown error" 
      };
    } catch (err) {
      console.error('Error loading Products Data:', err);
      return { 
        success: false, 
        message: err.response?.data?.message || "Network error" 
      };
    }
  };

  const getUsers = async () => {
    try {
      const response = await axios.get(
        'https://mindsinaction.com.na/api/mindsbackend/proccess.php',
        {
          params: { action: 'getUsers', token: `${process.env.REACT_APP_API_TOKEN}` },
          withCredentials: true
        }
      );

      if (response.data?.success) {
        return { 
          success: true, 
          data: response.data.data,
          message: response.data.message 
        };
      }
      return { 
        success: false, 
        message: response.data?.message || "Unknown error" 
      };
    } catch (err) {
      console.error('Error loading Users Data:', err);
      return { 
        success: false, 
        message: err.response?.data?.message || "Network error" 
      };
    }
  };

  const getStudents = async () => {
    try {
      const response = await axios.get(
        'https://mindsinaction.com.na/api/mindsbackend/proccess.php',
        {
          params: { action: 'getStudents', token: `${process.env.REACT_APP_API_TOKEN}` },
          withCredentials: true
        }
      );

      if (response.data?.success) {
        return { 
          success: true, 
          data: response.data.data,
          message: response.data.message 
        };
      }
      return { 
        success: false, 
        message: response.data?.message || "Unknown error" 
      };
    } catch (err) {
      console.error('Error loading Students Data:', err);
      return { 
        success: false, 
        message: err.response?.data?.message || "Network error" 
      };
    }
  };

  const getParents = async () => {
    try {
      const response = await axios.get(
        'https://mindsinaction.com.na/api/mindsbackend/proccess.php',
        {
          params: { action: 'getStudents', token: `${process.env.REACT_APP_API_TOKEN}` },
          withCredentials: true
        }
      );

      if (response.data?.success) {
        return { 
          success: true, 
          data: response.data.data,
          message: response.data.message 
        };
      }
      return { 
        success: false, 
        message: response.data?.message || "Unknown error" 
      };
    } catch (err) {
      console.error('Error loading Parents Data:', err);
      return { 
        success: false, 
        message: err.response?.data?.message || "Network error" 
      };
    }
  };

  const getSettings = async () => {
    try {
      const response = await axios.get(
        'https://mindsinaction.com.na/api/mindsbackend/proccess.php',
        {
          params: { action: 'getSettings', token: `${process.env.REACT_APP_API_TOKEN}` },
          withCredentials: true
        }
      );

      if (response.data?.success) {
        return { 
          success: true, 
          data: response.data.data,
          message: response.data.message 
        };
      }
      return { 
        success: false, 
        message: response.data?.message || "Unknown error" 
      };
    } catch (err) {
      console.error('Error loading Settings Data:', err);
      return { 
        success: false, 
        message: err.response?.data?.message || "Network error" 
      };
    }
  };

  const getBackgrounds = async () => {
    try {
      const response = await axios.get(
        'https://mindsinaction.com.na/api/mindsbackend/proccess.php',
        {
          params: { action: 'getBackgrounds', token: `${process.env.REACT_APP_API_TOKEN}` },
          withCredentials: true
        }
      );

      if (response.data?.success) {
        return { 
          success: true, 
          data: response.data.data,
          message: response.data.message 
        };
      }
      return { 
        success: false, 
        message: response.data?.message || "Unknown error" 
      };
    } catch (err) {
      console.error('Error loading Settings Data:', err);
      return { 
        success: false, 
        message: err.response?.data?.message || "Network error" 
      };
    }
  };

  const addBackground = async (credentials) => {
    try {
      const formData = new FormData();
      formData.append('title', credentials.title);
      formData.append('image', credentials.image);
      formData.append('page_name', credentials.page_name);
      formData.append('date_created', credentials.date_created);
      formData.append('action', 'addBackgrounds');
      formData.append('token', `${process.env.REACT_APP_API_TOKEN}`);

      const response = await axios.post(
        'https://mindsinaction.com.na/api/mindsbackend/proccess.php',
        {
          formData,
          withCredentials: true
        }
      );

      if (response.data?.success) {
        return { 
          success: true, 
          data: response.data.data,
          message: response.data.message 
        };
      }
      return { 
        success: false, 
        message: response.data?.message || "Unknown error" 
      };
    } catch (err) {
      console.error('Error loading Settings Data:', err);
      return { 
        success: false, 
        message: err.response?.data?.message || "Network error" 
      };
    }
  };

  const getServices = async () => {
    try {
      const response = await axios.get(
        'https://mindsinaction.com.na/api/mindsbackend/proccess.php',
        {
          params: { action: 'getServices', token: `${process.env.REACT_APP_API_TOKEN}` },
          withCredentials: true
        }
      );

      if (response.data?.success) {
        return { 
          success: true, 
          data: response.data.data,
          message: response.data.message 
        };
      }
      return { 
        success: false, 
        message: response.data?.message || "Unknown error" 
      };
    } catch (err) {
      console.error('Error loading Settings Data:', err);
      return { 
        success: false, 
        message: err.response?.data?.message || "Network error" 
      };
    }
  };

  const getModules = async () => {
    try {
      const response = await axios.get(
        'https://mindsinaction.com.na/api/mindsbackend/proccess.php',
        {
          params: { action: 'getModules', token: `${process.env.REACT_APP_API_TOKEN}` },
          withCredentials: true
        }
      );

      if (response.data?.success) {
        return { 
          success: true, 
          data: response.data.data,
          message: response.data.message 
        };
      }
      return { 
        success: false, 
        message: response.data?.message || "Unknown error" 
      };
    } catch (err) {
      console.error('Error loading Settings Data:', err);
      return { 
        success: false, 
        message: err.response?.data?.message || "Network error" 
      };
    }
  };

  const getTestmonials = async () => {
    try {
      const response = await axios.get(
        'https://mindsinaction.com.na/api/mindsbackend/proccess.php',
        {
          params: { action: 'getTestmonials', token: `${process.env.REACT_APP_API_TOKEN}` },
          withCredentials: true
        }
      );

      if (response.data?.success) {
        return { 
          success: true, 
          data: response.data.data,
          message: response.data.message 
        };
      }
      return { 
        success: false, 
        message: response.data?.message || "Unknown error" 
      };
    } catch (err) {
      console.error('Error loading Settings Data:', err);
      return { 
        success: false, 
        message: err.response?.data?.message || "Network error" 
      };
    }
  };

  const getAboutus = async () => {
    try {
      const response = await axios.get(
        'https://mindsinaction.com.na/api/mindsbackend/proccess.php',
        {
          params: { action: 'getAboutus', token: `${process.env.REACT_APP_API_TOKEN}` },
          withCredentials: true
        }
      );

      if (response.data?.success) {
        return { 
          success: true, 
          data: response.data.data,
          message: response.data.message 
        };
      }
      return { 
        success: false, 
        message: response.data?.message || "Unknown error" 
      };
    } catch (err) {
      console.error('Error loading Settings Data:', err);
      return { 
        success: false, 
        message: err.response?.data?.message || "Network error" 
      };
    }
  };

  const getWhysteam = async () => {
    try {
      const response = await axios.get(
        'https://mindsinaction.com.na/api/mindsbackend/proccess.php',
        {
          params: { action: 'getWhysteam', token: `${process.env.REACT_APP_API_TOKEN}` },
          withCredentials: true
        }
      );

      if (response.data?.success) {
        return { 
          success: true, 
          data: response.data.data,
          message: response.data.message 
        };
      }
      return { 
        success: false, 
        message: response.data?.message || "Unknown error" 
      };
    } catch (err) {
      console.error('Error loading Settings Data:', err);
      return { 
        success: false, 
        message: err.response?.data?.message || "Network error" 
      };
    }
  };

  const getDepartments = async () => {
    try {
      const response = await axios.get(
        'https://mindsinaction.com.na/api/mindsbackend/proccess.php',
        {
          params: { action: 'getDepartments', token: `${process.env.REACT_APP_API_TOKEN}` },
          withCredentials: true
        }
      );

      if (response.data?.success) {
        return { 
          success: true, 
          data: response.data.data,
          message: response.data.message 
        };
      }
      return { 
        success: false, 
        message: response.data?.message || "Unknown error" 
      };
    } catch (err) {
      console.error('Error loading Settings Data:', err);
      return { 
        success: false, 
        message: err.response?.data?.message || "Network error" 
      };
    }
  };

  // ###################################################################################################
  const getLeaves = async () => {
    try {
      const response = await axios.get(
        'https://mindsinaction.com.na/api/mindsbackend/proccess.php',
        {
          params: { action: 'getLeaves', token: `${process.env.REACT_APP_API_TOKEN}` },
          withCredentials: true
        }
      );

      if (response.data?.success) {
        return { 
          success: true, 
          data: response.data.data,
          message: response.data.message 
        };
      }
      return { 
        success: false, 
        message: response.data?.message || "Unknown error" 
      };
    } catch (err) {
      console.error('Error loading Settings Data:', err);
      return { 
        success: false, 
        message: err.response?.data?.message || "Network error" 
      };
    }
  };

  const getLaptops = async () => {
    try {
      const response = await axios.get(
        'https://mindsinaction.com.na/api/mindsbackend/proccess.php',
        {
          params: { action: 'getLaptops', token: `${process.env.REACT_APP_API_TOKEN}` },
          withCredentials: true
        }
      );

      if (response.data?.success) {
        return { 
          success: true, 
          data: response.data.data,
          message: response.data.message 
        };
      }
      return { 
        success: false, 
        message: response.data?.message || "Unknown error" 
      };
    } catch (err) {
      console.error('Error loading Settings Data:', err);
      return { 
        success: false, 
        message: err.response?.data?.message || "Network error" 
      };
    }
  };

  const getChargers = async () => {
    try {
      const response = await axios.get(
        'https://mindsinaction.com.na/api/mindsbackend/proccess.php',
        {
          params: { action: 'getChargers', token: `${process.env.REACT_APP_API_TOKEN}` },
          withCredentials: true
        }
      );

      if (response.data?.success) {
        return { 
          success: true, 
          data: response.data.data,
          message: response.data.message 
        };
      }
      return { 
        success: false, 
        message: response.data?.message || "Unknown error" 
      };
    } catch (err) {
      console.error('Error loading Settings Data:', err);
      return { 
        success: false, 
        message: err.response?.data?.message || "Network error" 
      };
    }
  };

  const getMonitors = async () => {
    try {
      const response = await axios.get(
        'https://mindsinaction.com.na/api/mindsbackend/proccess.php',
        {
          params: { action: 'getMonitors', token: `${process.env.REACT_APP_API_TOKEN}` },
          withCredentials: true
        }
      );

      if (response.data?.success) {
        return { 
          success: true, 
          data: response.data.data,
          message: response.data.message 
        };
      }
      return { 
        success: false, 
        message: response.data?.message || "Unknown error" 
      };
    } catch (err) {
      console.error('Error loading Settings Data:', err);
      return { 
        success: false, 
        message: err.response?.data?.message || "Network error" 
      };
    }
  };

  const getComputers = async () => {
    try {
      const response = await axios.get(
        'https://mindsinaction.com.na/api/mindsbackend/proccess.php',
        {
          params: { action: 'getComputers', token: `${process.env.REACT_APP_API_TOKEN}` },
          withCredentials: true
        }
      );

      if (response.data?.success) {
        return { 
          success: true, 
          data: response.data.data,
          message: response.data.message 
        };
      }
      return { 
        success: false, 
        message: response.data?.message || "Unknown error" 
      };
    } catch (err) {
      console.error('Error loading Settings Data:', err);
      return { 
        success: false, 
        message: err.response?.data?.message || "Network error" 
      };
    }
  };

  const getBlogs = async () => {
    try {
      const response = await axios.get(
        'https://mindsinaction.com.na/api/mindsbackend/proccess.php',
        {
          params: { action: 'getBlogs', token: `${process.env.REACT_APP_API_TOKEN}` },
          withCredentials: true
        }
      );

      if (response.data?.success) {
        return { 
          success: true, 
          data: response.data.data,
          message: response.data.message 
        };
      }
      return { 
        success: false, 
        message: response.data?.message || "Unknown error" 
      };
    } catch (err) {
      console.error('Error loading Settings Data:', err);
      return { 
        success: false, 
        message: err.response?.data?.message || "Network error" 
      };
    }
  };

  const getAddverts = async () => {
    try {
      const response = await axios.get(
        'https://mindsinaction.com.na/api/mindsbackend/proccess.php',
        {
          params: { action: 'getAddverts', token: `${process.env.REACT_APP_API_TOKEN}` },
          withCredentials: true
        }
      );

      if (response.data?.success) {
        return { 
          success: true, 
          data: response.data.data,
          message: response.data.message 
        };
      }
      return { 
        success: false, 
        message: response.data?.message || "Unknown error" 
      };
    } catch (err) {
      console.error('Error loading Settings Data:', err);
      return { 
        success: false, 
        message: err.response?.data?.message || "Network error" 
      };
    }
  };

  const getBorrows = async () => {
    try {
      const response = await axios.get(
        'https://mindsinaction.com.na/api/mindsbackend/proccess.php',
        {
          params: { action: 'getBorrows', token: `${process.env.REACT_APP_API_TOKEN}` },
          withCredentials: true
        }
      );

      if (response.data?.success) {
        return { 
          success: true, 
          data: response.data.data,
          message: response.data.message 
        };
      }
      return { 
        success: false, 
        message: response.data?.message || "Unknown error" 
      };
    } catch (err) {
      console.error('Error loading Settings Data:', err);
      return { 
        success: false, 
        message: err.response?.data?.message || "Network error" 
      };
    }
  };

  const getReturns = async () => {
    try {
      const response = await axios.get(
        'https://mindsinaction.com.na/api/mindsbackend/proccess.php',
        {
          params: { action: 'getReturns', token: `${process.env.REACT_APP_API_TOKEN}` },
          withCredentials: true
        }
      );

      if (response.data?.success) {
        return { 
          success: true, 
          data: response.data.data,
          message: response.data.message 
        };
      }
      return { 
        success: false, 
        message: response.data?.message || "Unknown error" 
      };
    } catch (err) {
      console.error('Error loading Settings Data:', err);
      return { 
        success: false, 
        message: err.response?.data?.message || "Network error" 
      };
    }
  };

  const getTables = async () => {
    try {
      const response = await axios.get(
        'https://mindsinaction.com.na/api/mindsbackend/proccess.php',
        {
          params: { action: 'getTables', token: `${process.env.REACT_APP_API_TOKEN}` },
          withCredentials: true
        }
      );

      if (response.data?.success) {
        return { 
          success: true, 
          data: response.data.data,
          message: response.data.message 
        };
      }
      return { 
        success: false, 
        message: response.data?.message || "Unknown error" 
      };
    } catch (err) {
      console.error('Error loading Settings Data:', err);
      return { 
        success: false, 
        message: err.response?.data?.message || "Network error" 
      };
    }
  };

  const getChairs = async () => {
    try {
      const response = await axios.get(
        'https://mindsinaction.com.na/api/mindsbackend/proccess.php',
        {
          params: { action: 'getChairs', token: `${process.env.REACT_APP_API_TOKEN}` },
          withCredentials: true
        }
      );

      if (response.data?.success) {
        return { 
          success: true, 
          data: response.data.data,
          message: response.data.message 
        };
      }
      return { 
        success: false, 
        message: response.data?.message || "Unknown error" 
      };
    } catch (err) {
      console.error('Error loading Settings Data:', err);
      return { 
        success: false, 
        message: err.response?.data?.message || "Network error" 
      };
    }
  };

  // ###########################################################################################################
  const addData = async (endpoint, tableName, credentials) => {
    try {
      const formData = new FormData();
      
      // Append all provided credentials to formData
      for (const key in credentials) {
        if (credentials.hasOwnProperty(key)) {
          formData.append(key, credentials[key]);
        }
      }

      // System fields
      formData.append('action', `addDatas`);
      formData.append('table', tableName);
      formData.append('token', process.env.REACT_APP_API_TOKEN);

      const response = await axios.post(
        `https://mindsinaction.com.na/api/mindsbackend/${endpoint}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          },
          withCredentials: true
        }
      );

      if (response.data?.success) {
        return { 
          success: true, 
          data: response.data.data,
          message: response.data.message 
        };
      }
      return { 
        success: false, 
        message: response.data?.message || "Unknown error",
        errors: response.data?.errors
      };
    } catch (err) {
      console.error(`Error adding ${tableName} Data:`, err);
      return { 
        success: false, 
        message: err.response?.data?.message || "Network error",
        status: err.response?.status
      };
    }
  };

  const resetPassword = async (endpoint, tableName, credentials) => {
    try {
      const formData = new FormData();

      for (const key in credentials) {
        if (credentials.hasOwnProperty(key)) {
          formData.append(key, credentials[key]);
        }
      }

      // System fields
      formData.append('action', `staffsPasswordReset`);
      formData.append('table', tableName);
      formData.append('token', process.env.REACT_APP_API_TOKEN);

      const response = await axios.post(
        `https://mindsinaction.com.na/api/mindsbackend/${endpoint}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          },
          withCredentials: true
        }
      );

      if (response.data?.success) {
        return { 
          success: true, 
          data: response.data.data,
          message: response.data.message 
        };
      }
      return { 
        success: false, 
        message: response.data?.message || "Unknown error",
        errors: response.data
      };
    } catch (err) {
      console.error(`Error reseting ${tableName} Data:`, err);
      return { 
        success: false, 
        message: err.response?.data?.message || "Network error",
        status: err.response?.status
      };
    }
  };

  const updateData = async (endpoint, tableName, credentials, id) => {
    try {
      const formData = new FormData();
      
      // Append all provided credentials to formData
      for (const key in credentials) {
        if (credentials.hasOwnProperty(key)) {
          formData.append(key, credentials[key]);
        }
      }

      // System fields
      formData.append('action', 'updateDatas');
      formData.append('table', tableName);
      formData.append('token', process.env.REACT_APP_API_TOKEN);
      formData.append('id', id); // ID of the record to update

      console.log("responce: ",formData.image);
      const response = await axios.post(
        `https://mindsinaction.com.na/api/mindsbackend/${endpoint}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          },
          withCredentials: true
        }
      );

      if (response.data?.success) {
        return { 
          success: true, 
          data: response.data.data,
          message: response.data.message 
        };
      } 
      else{
        console.log("responce: ",response);
      }
      return { 
        success: false, 
        message: response.data?.message || "Unknown error",
        errors: response.data?.errors
      };
    } catch (err) {
      console.error(`Error updating ${tableName} Data:`, err);
      return { 
        success: false, 
        message: err.response?.data?.message || "Network error",
        status: err.response?.status
      };
    }
  };

  const getData = async (endpoint, tableName, id = null) => {
    try {
      const formData = new FormData();
      formData.append('action', 'getDatas'); // Confirm correct action name
      formData.append('table', tableName);  // Confirm correct table param name
      formData.append('token', process.env.REACT_APP_API_TOKEN);

      // Append additional filters (if any)
      if (id !== null) {
        formData.append('id', id);
      }

      const response = await axios.post(
        `https://mindsinaction.com.na/api/mindsbackend/${endpoint}`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          withCredentials: true,
        }
      );

      if (!response.data.success) {
        //console.log(response.data.data);
        
        throw new Error(response.data.message || "API rejected request");
      }

      return {
        success: true,
        data: response.data.data || [],
        message: response.data.message,
      };
    } catch (err) {
      return {
        success: false,
        message: err.response?.data?.message || err.message || "Request failed",
        errors: err.response?.data?.errors || [],
      };
    }
  };

  const deleteData = async (endpoint, tableName, id) => {
    try {
      const formData = new FormData();
      
      // System fields
      formData.append('action', 'deleteData');
      formData.append('table', tableName);
      formData.append('token', process.env.REACT_APP_API_TOKEN);
      formData.append('id', id); // ID of the record to delete

      const response = await axios.post(
        `https://mindsinaction.com.na/api/mindsbackend/${endpoint}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          },
          withCredentials: true
        }
      );

      if (response.data?.success) {
        return { 
          success: true, 
          data: response.data.data,
          message: response.data.message 
        };
      }
      return { 
        success: false, 
        message: response.data?.message || "Unknown error",
        errors: response.data?.errors
      };
    } catch (err) {
      console.error(`Error deleting ${tableName}:`, err);
      return { 
        success: false, 
        message: err.response?.data?.message || "Network error",
        status: err.response?.status
      };
    }
  };

  // ##############################################################################################################

  return (
    <AuthContext.Provider value={{ ...authState, login, logout, revalidateWithServer, getTeams, getProducts, getStudents, getUsers, getSettings, getParents, getBackgrounds, addBackground, getServices, getModules, getTestmonials, getAboutus, getWhysteam, getDepartments, getAddverts, getLeaves, getBlogs, getBorrows, getReturns, getTables, getChairs, addData, getData, updateData, deleteData, resetPassword }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);